/**
 * DeductionsListView Organism
 * Pure presentation component for deductions list grouped by worker/gang.
 * Follows PaymentsListView pattern with ListViewShell + collapsible groups.
 */

import { useCallback, useMemo } from 'react';
import { Box, Alert, Collapse } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useShallow } from 'zustand/react/shallow';
import { DeductionListItem } from './DeductionListItem';
import { DeductionGroupHeader } from './DeductionGroupHeader';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';
import { groupDeductionsByWorker } from '../utils/groupDeductionsByWorker';
import { groupDeductionsBySite } from '../utils/groupDeductionsBySite';
import { useDeductionsUIStore } from '../stores/deductionsUIStore';
import type { AdminDeductionRow } from '@billda/api';

export interface DeductionsListViewProps {
  deductions: AdminDeductionRow[];
  isLoading: boolean;
  error: Error | null;
  onDeductionClick?: (deduction: AdminDeductionRow) => void;
}

export function DeductionsListView({
  deductions,
  isLoading,
  error,
  onDeductionClick,
}: DeductionsListViewProps) {
  const { collapsedGroups, toggleGroup, groupingMode } = useDeductionsUIStore(
    useShallow(state => ({
      collapsedGroups: state.collapsedGroups,
      toggleGroup: state.toggleGroup,
      groupingMode: state.groupingMode,
    }))
  );

  const groups = useMemo(
    () =>
      groupingMode === 'site'
        ? groupDeductionsBySite(deductions)
        : groupDeductionsByWorker(deductions),
    [deductions, groupingMode]
  );

  const handleToggle = useCallback(
    (groupName: string) => () => toggleGroup(groupName),
    [toggleGroup]
  );

  if (error && !isLoading && deductions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load deductions. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <ListViewShell
        itemCount={deductions.length}
        loading={isLoading}
        emptyIcon={
          <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        }
        emptyTitle="No deductions this week"
        emptyHelperText="Deductions like crane hire, scaffolding, and materials show here once they're added to pots in the selected period."
        emptyMessage=""
        data-testid="deductions-list"
      >
        {groups.map((group, index) => {
          const isCollapsed = collapsedGroups.has(group.groupName);
          const panelId = `deduction-group-panel-${
            group.groupName
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '') || index
          }`;
          return (
            <Box key={group.groupName}>
              <DeductionGroupHeader
                groupName={group.groupName}
                count={group.deductions.length}
                totalAmount={group.totalAmount}
                expanded={!isCollapsed}
                onToggle={handleToggle(group.groupName)}
                groupedBySite={groupingMode === 'site'}
                panelId={panelId}
                data-testid={`deduction-group-${group.groupName}`}
              />
              <Collapse in={!isCollapsed} unmountOnExit>
                <Box id={panelId}>
                  {group.deductions.map((deduction, index) => (
                    <DeductionListItem
                      key={deduction.id}
                      deduction={deduction}
                      onClick={onDeductionClick}
                      data-testid={`deduction-row-${index}`}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </ListViewShell>
    </Box>
  );
}
